import { SelectField } from '@platform/ui-kit';
import type { CSSProperties } from 'react';

/**
 * The Integration Group picker for every Program Planning screen that filters by group
 * (owner request 2026-11-04: Ctrl-click to pick several groups, plus "select all", the same
 * on every Roadmap Planning and Resource Planning screen).
 *
 * It is the platform's `SelectField` in multiple mode — the Project/Opportunity picker's
 * design: plain click picks one and closes, Ctrl-click (Cmd on a Mac) adds or removes, the
 * first row selects or clears all, and each group reads bold ID - description. No checkboxes.
 *
 * `value` is the list of picked group IDs. What an empty list means is the screen's call:
 * screens that load nothing until a group is chosen show the prompt; filter screens treat
 * "none picked" as "no filter" and say so in the placeholder.
 */
export interface GroupOption {
  id: string;
  description?: string | null;
}

export const ALL_GROUPS_LABEL = 'All Integration Groups';

/** Read by screen readers, not shown: a picker with no visible label still needs a name (axe "button-name"). */
const HIDDEN: CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

export function GroupMultiSelect({
  groups,
  value,
  onChange,
  label = 'Integration Group',
  placeholder = 'Select Integration Group(s)',
  testId,
  style,
}: {
  groups: GroupOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  placeholder?: string;
  testId?: string;
  style?: CSSProperties;
}) {
  return (
    <div data-testid={testId} style={{ display: 'inline-flex' }}>
      <SelectField
        multiple
        label={label || <span style={HIDDEN}>Integration Group</span>}
        width="lg"
        placeholder={placeholder}
        floating
        allLabel={ALL_GROUPS_LABEL}
        value={value}
        onChange={onChange}
        options={groups.map((g) => ({ value: g.id, id: g.id, description: g.description ?? null }))}
        style={{ alignItems: 'center', ...(label ? {} : { gap: 0 }), ...style }}
      />
    </div>
  );
}

/**
 * What to ask the server for, in the shape the routes already understand: '' (nothing
 * picked), one group's ID, or 'ALL'. Several groups load 'ALL' and are narrowed on screen
 * with `inPicked`, so no route had to change.
 */
export function groupFetchKey(picked: string[], allKey = 'ALL'): string {
  if (picked.length === 0) return '';
  if (picked.length === 1) return picked[0] ?? '';
  return allKey;
}

/** Row test for a picked set. An empty set matches everything (filter screens). */
export function inPicked(picked: string[], groupId: string | null | undefined): boolean {
  return picked.length === 0 || (groupId != null && picked.includes(groupId));
}
